import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Alert, Typography, LinearProgress } from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../services/apiService';
import { generateJobName } from '../utils/jobNaming';
import PracticeWorkflowSelector from './PracticeWorkflowSelector';
import FileUploader from './FileUploader';
import WorkflowUIElements from './WorkflowUIElements';
import FileInputCategorizer, { RequiredInput } from './FileInputCategorizer';

interface WorkflowFormProps {
  files?: File[];
  onExecute?: (executionArn: string) => void;
  onJobCreated?: () => void;
  disabled?: boolean;
  preselectedPractice?: string;
  preselectedWorkflows?: string[];
}

export default function WorkflowForm({ files = [], onExecute, onJobCreated, disabled = false, preselectedPractice, preselectedWorkflows }: WorkflowFormProps) {
  const queryClient = useQueryClient();
  const [practiceId, setPracticeId] = useState(preselectedPractice || '');
  const [workflowIds, setWorkflowIds] = useState<string[]>(preselectedWorkflows || []);
  const [number, setNumber] = useState('');
  const [localFiles, setLocalFiles] = useState<File[]>(files);
  const [fileValidationErrors, setFileValidationErrors] = useState<string[]>([]);
  const [uiElementValues, setUIElementValues] = useState<Record<string, any>>({});
  const [fileInputMapping, setFileInputMapping] = useState<Record<string, string>>({});
  
  // Upload progress state
  const [uploadProgress, setUploadProgress] = useState<{
    currentFile: number;
    totalFiles: number;
    fileName: string;
    percent: number;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Get workflow details for validation
  const { data: workflowsData } = useQuery({
    queryKey: ['workflows', practiceId],
    queryFn: () => apiService.getWorkflowsForPractice(practiceId),
    enabled: !!practiceId
  });

  // Get recipe requirements for UI elements (single workflow only)
  const { data: recipeRequirements } = useQuery({
    queryKey: ['recipe-requirements', practiceId, workflowIds[0]],
    queryFn: () => apiService.getRecipeRequirements(practiceId, workflowIds[0]),
    enabled: !!(practiceId && workflowIds.length === 1)
  });

  // Generate a job name if not set
  useEffect(() => {
    if (!number) {
      setNumber(generateJobName());
    }
  }, [number]);

  // Validate files against selected workflows
  useEffect(() => {
    if (!workflowsData || workflowIds.length === 0) {
      setFileValidationErrors([]);
      return;
    }

    const allFiles = files.length > 0 ? files : localFiles;
    if (allFiles.length === 0) {
      setFileValidationErrors([]);
      return;
    }

    const errors: string[] = [];
    const selectedWorkflows = workflowsData.filter((w: any) => workflowIds.includes(w.id));

    // Check each file against each selected workflow
    for (const file of allFiles) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      
      for (const workflow of selectedWorkflows) {
        const allowedTypes = workflow.fileTypes?.allowed_input || [];
        const isAllowed = allowedTypes.length === 0 || allowedTypes.includes(fileExtension || '');
        
        if (!isAllowed) {
          const workflowName = workflow.ui?.displayName || workflow.name;
          errors.push(`File "${file.name}" (${fileExtension?.toUpperCase()}) is not compatible with "${workflowName}" (accepts: ${allowedTypes.join(', ').toUpperCase()})`);
        }
      }
    }

    setFileValidationErrors(errors);
  }, [workflowIds, workflowsData, files, localFiles]);

  // Execute workflow mutation
  const executeMutation = useMutation({
    mutationFn: (params: {
      practice: string;
      workflows: string[];
      number: string;
      input_file_s3_uris: string[];
      workflow_data?: Record<string, any>;
    }) => apiService.executeWorkflow(params),
    onSuccess: (data) => {
      // immediately refresh job list so the new card appears without waiting for poll
      queryClient.invalidateQueries({ queryKey: ['jobs'] });
      setIsSubmitting(false);
      // regenerate job name so a subsequent submit gets a unique name
      setNumber(generateJobName());
      onExecute?.(data.executionArn);
      onJobCreated?.();
    },
    onError: () => {
      setIsSubmitting(false);
    }
  });

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    try {
      // Use default job name if empty
      const jobName = number.trim() || generateJobName();
      
      // First upload files to S3
      const filesToUpload = files.length > 0 ? files : localFiles;
      
      // Calculate total file size to determine if we need progress
      const totalSize = filesToUpload.reduce((acc, f) => acc + f.size, 0);
      const needsProgress = totalSize > 10 * 1024 * 1024; // 10MB threshold
      
      // Progress callback for large uploads
      const onProgress = needsProgress 
        ? (fileIndex: number, progress: number, fileName: string) => {
            setUploadProgress({
              currentFile: fileIndex + 1,
              totalFiles: filesToUpload.length,
              fileName,
              percent: progress
            });
          }
        : undefined;
      
      const uploadResult = await apiService.uploadFiles(
        filesToUpload,
        practiceId,
        jobName,
        onProgress
      );
      
      // Clear progress after upload completes
      setUploadProgress(null);

      console.log('Upload result:', uploadResult);

      // Then execute workflow with S3 URIs
      executeMutation.mutate({
        practice: practiceId,
        workflows: workflowIds,
        number: jobName,
        input_file_s3_uris: uploadResult.s3Uris,
        workflow_data: {
          ...uiElementValues,
          ...(Object.keys(fileInputMapping).length > 0 ? { file_input_mapping: fileInputMapping } : {})
        }
      });
    } catch (error) {
      console.error('Upload or execution failed:', error);
      setUploadProgress(null); // Clear progress on error
      setIsSubmitting(false);
    }
  };

  const isUploading = uploadProgress !== null;
  const isExecuting = executeMutation.isPending;
  const isProcessing = isSubmitting || isUploading || isExecuting;
  const hasValidationErrors = fileValidationErrors.length > 0;

  // Check if required inputs are satisfied
  const requiredInputs: RequiredInput[] = recipeRequirements?.required_inputs || [];
  const hasRequiredInputs = requiredInputs.length > 0;
  const allFiles = files.length > 0 ? files : localFiles;
  const assignedIdentifiers = new Set(Object.values(fileInputMapping));
  const missingRequired = hasRequiredInputs && requiredInputs.some(
    (inp: RequiredInput) => inp.required !== false && !assignedIdentifiers.has(inp.identifier)
  );

  // Check if required UI elements (e.g. party_role, address_list) are filled in.
  // elements may store their value under a custom field_name (e.g. address_list -> addresses)
  // rather than under el.type, so fall back to field_name when checking.
  const missingRequiredUIElements = (recipeRequirements?.ui_elements || []).some(
    (el: any) => {
      if (el.config?.required !== true) return false;
      const key = el.config?.field_name || el.type;
      const val = uiElementValues[key];
      // treat empty arrays and empty strings as "missing"
      if (Array.isArray(val)) return val.length === 0;
      return !val;
    }
  );

  // workflows that declare no input file types (e.g. cortese-list) take their input from
  // UI elements instead of uploads, so we must not require a file to submit.
  const supportedFormats: string[] = recipeRequirements?.supported_formats || [];
  const workflowRequiresFiles = supportedFormats.length > 0;
  const noFilesSelected = files.length === 0 && localFiles.length === 0;
  const blockedByMissingFiles = workflowRequiresFiles && noFilesSelected;

  return (
    <Box>
      {files.length === 0 && !preselectedPractice && (
        <Box sx={{ mb: 3 }}>
          <FileUploader 
            files={localFiles} 
            setFiles={setLocalFiles} 
            showNextButton={false}
            practice={practiceId}
            jobNumber={number}
          />
        </Box>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        {!preselectedPractice && (
          <PracticeWorkflowSelector
            selectedPractice={practiceId}
            selectedWorkflows={workflowIds}
            onPracticeChange={setPracticeId}
            onWorkflowChange={setWorkflowIds}
            disabled={disabled || isProcessing}
          />
        )}

        <TextField
          label="Job Name"
          value={number}
          onChange={(e) => setNumber(e.target.value)}
          placeholder="09-24_1234"
          disabled={disabled || isProcessing}
          fullWidth
          sx={{ mt: 2 }}
        />

        {/* Show UI elements for single workflow selection */}
        {workflowIds.length === 1 && recipeRequirements?.ui_elements && recipeRequirements.ui_elements.length > 0 && (
          <WorkflowUIElements
            elements={recipeRequirements.ui_elements}
            values={uiElementValues}
            onChange={(elementType, value) => {
              setUIElementValues(prev => ({ ...prev, [elementType]: value }));
            }}
            disabled={disabled || isProcessing}
          />
        )}

        {/* File-to-document-type mapper */}
        {hasRequiredInputs && allFiles.length > 0 && (
          <FileInputCategorizer
            files={allFiles}
            requiredInputs={requiredInputs}
            onChange={setFileInputMapping}
            disabled={disabled || isProcessing}
          />
        )}
      </Box>

      {fileValidationErrors.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Alert severity="error" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              File Type Compatibility Issues:
            </Typography>
            {fileValidationErrors.map((error, index) => (
              <Typography key={index} variant="body2" sx={{ mb: 0.5 }}>
                • {error}
              </Typography>
            ))}
          </Alert>
        </Box>
      )}

      {/* Upload Progress Indicator */}
      {uploadProgress && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              Uploading {uploadProgress.totalFiles > 1 
                ? `file ${uploadProgress.currentFile} of ${uploadProgress.totalFiles}` 
                : 'file'}: {uploadProgress.fileName.length > 40 
                  ? `${uploadProgress.fileName.substring(0, 37)}...` 
                  : uploadProgress.fileName}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {uploadProgress.percent}%
            </Typography>
          </Box>
          <LinearProgress 
            variant="determinate" 
            value={uploadProgress.percent} 
            sx={{ height: 8, borderRadius: 4 }}
          />
          {uploadProgress.percent === 100 && (
            <Typography variant="caption" color="success.main" sx={{ mt: 0.5, display: 'block' }}>
              Upload complete, starting workflow...
            </Typography>
          )}
        </Box>
      )}

      <Button
        variant="contained"
        onClick={handleSubmit}
        disabled={disabled || !practiceId || workflowIds.length === 0 || isProcessing || blockedByMissingFiles || hasValidationErrors || missingRequired || missingRequiredUIElements}
        size="large"
      >
        {isUploading ? 'Uploading Files...' : 
         isExecuting ? 'Starting Execution...' : 
         'Execute Workflow'}
      </Button>
    </Box>
  );
}